import { Trans, useTranslation } from 'next-i18next'
// Dependencies.
import styled from 'styled-components'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import { Page } from '../components/Page'
import { GetStarted } from '../components/GetStarted'
import { Section } from '../components/Section'
import { H1 } from '../components/H1'
import { H2 } from '../components/H2'
import { H4 } from '../components/H4'
import { P } from '../components/P'
import { Hyperlink } from '../components/Hyperlink'

// Terms Page.
const TermsPage = () => {
  const { t } = useTranslation('common')
  return (
    <Page>
      <Section>
        <Div>
          <H1>{t(`KLYK TERMS OF USE`, `KLYK TERMS OF USE`)}</H1>
          <P>
            <Trans i18nKey="terms1218611373">
              Welcome to the Klyk Terms of Use (the “<strong>Terms</strong>”).
              Klyk is owned and operated by Apogee Management Services
              (Australia) Pty Ltd ( hereafter &quot;<strong>Klyk</strong>&quot;
              (hereafter “<strong>Klyk</strong>”, “<strong>we</strong>”, “
              <strong>us</strong>”, “<strong>our</strong>”). The Klyk Service is
              a platform which helps users manage the facilitation of and
              participation in livestreams and events with their friends,
              family, and community. By registering with the Klyk website
              located at www.ourklyk.com and other websites, software
              applications, services, devices (including, but not limited to,
              any mobile devices) or networks owned or controlled by Klyk that
              allow for the distribution and reception of video, audio, and
              other content (collectively, the{' '}
              <strong>&quot;Klyk Service&quot;</strong>) or by accessing any
              content or material that is made available by the Klyk Service,
              you are entering into a binding agreement with Klyk.
            </Trans>
            <br />
            <br />
            <Trans i18nKey="terms-156271510">
              By using the Klyk Service, you agree to be bound by these Terms
              and our policies made available on the Klyk website (including but
              not limited to our Privacy Policy, which is available here
              https://ourklyk.com/privacy) and Safety Policy, which is available
              here www.ourklyk.com/safety) (<strong>Policies</strong> which are
              incorporated into these Terms by reference. By accepting our
              Terms, you warrant that you are capable of entering into and
              performing the obligations under these Terms and the Policies.
            </Trans>
          </P>
          <H4>
            {t(
              `terms-226818330`,
              `IF YOU DO NOT AGREE  WITH THE TERMS OF USE YOU MUST NOT ACCESS, USE OR OPERATE ANY PART OR WHOLE OF THE KLYK SERVICE.`,
            )}
          </H4>

          {/* General */}
          <H2>{t(`1. General`, `1. General`)}</H2>
          <ul>
            <li>
              <P>
                {t(
                  `terms-189363106`,
                  `Klyk reserves the right to change these Terms and any of the Policies at any time by updating these Terms and/or Policies (as applicable) on the Klyk Service. Except where required by law, we are under no obligation to provide you with individual notice of any changes. As such, we recommend that you regularly check the Klyk Service  for updates. Your continued use of the Klyk Service after such changes will signify your acceptance of the amended Terms and Policies (as applicable). If you do not agree with any of the amended Terms or Policies then please do not continue to access or use the Klyk Service.`,
                )}
              </P>
            </li>
            <li>
              <P>{t(`terms30826306`, `By using the Klyk Service:`)}</P>
              <ul>
                <li>
                  <P>
                    {t(
                      `terms-1112984930`,
                      `You warrant that you are over the age of 18 and have the right and authority to enter into a binding contract with us and are not prohibited from doing so under any applicable laws, rules, and regulations;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms2078363540`,
                      `If you are not 18 years or older you must not use the Klyk Service without your parent or guardian’s express consent and provided your parent or guardian warrants and undertakes that your parent or guardian agrees to these Terms and undertakes to be liable for the same on your behalf;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-1103330808`,
                      `If you are under age 13, you may not, under any circumstances, or for any reason, use any part of the Klyk Service without a parent or guardian present; and`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1937696719`,
                      `We may, in our sole discretion, refuse to offer access to any part of the Klyk Service to any person or entity and may change the eligibility criteria for access to the Klyk Service at any time;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms781268265`,
                      `You acknowledge that we collect personal information about you in order to create your Klyk Account in accordance with our Privacy Policy.`,
                    )}
                  </P>
                </li>
              </ul>
            </li>
          </ul>

          {/* Registration */}
          <H2>{t(`2. Registration`, `2. Registration`)}</H2>
          <ul>
            <li>
              <P>
                <Trans i18nKey="terms1847518439">
                  In order to use the Klyk Service, you will be required to
                  register your personal details to set up an account (your “
                  <strong>Klyk Account</strong>”). This can be done via personal
                  email. You will be able to modify your Klyk Account details by
                  going into the account settings in your profile.
                </Trans>
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms103564563`,
                  `You warrant that you have read and agree to our Policies before creating a Klyk Account.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-464973553`,
                  `You agree that you will be the sole user of your Klyk Account and that you will not allow any third parties to access your Klyk Account.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms1381267888`,
                  `If you register and create a Klyk Account, you agree to be responsible and/or liable for maintaining the confidentiality and security of passwords and other account identifiers which you nominate, and all activities that occur under such password or account identifiers.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms545063989`,
                  `You agree to notify Klyk of any loss of your password or your Klyk Account identifiers and any unauthorised use of your password or Klyk Account identifiers by emailing us at admin@ourklyk.com.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms1478862578`,
                  `By registering for a Klyk Account, you further agree that:`,
                )}
              </P>
              <ul>
                <li>
                  <P>
                    {t(
                      `terms-1594828851`,
                      `you will not create more than one personal Klyk Account;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms725496751`,
                      `you will not create a Klyk Account for anyone but yourself;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms725496751`,
                      `you will not create a Klyk Account for anyone but yourself;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms626428703`,
                      `if we disable your Klyk Account for any reason, that you will not attempt to make a new Klyk Account; and`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1853412897`,
                      `you will not transfer your Klyk Account to any third party without first gaining our written permission.`,
                    )}
                  </P>
                </li>
              </ul>
            </li>
          </ul>

          {/* Use of Klyk */}
          <H2>{t(`terms-1139647117`, `3. Use of Klyk Service`)}</H2>
          <ul>
            <li>
              <P>
                {t(
                  `terms714018850`,
                  `Subject to these Terms, Klyk grants you a limited, non-exclusive, non-transferable, revocable licence to use the Klyk Service for the primary purpose of (i) facilitating a livestream or event and communicating with other users who may or intend to attend your livestream or event; and (ii) communicating with other users facilitating a livestream or event and attending livestreams and events. Your licence shall remain in effect until terminated by you or Klyk. You warrant that you will not copy, reproduce, communicate to the public, perform to the public, redistribute, transfer, exploit or otherwise use the Klyk Service whatsoever without Klyk’s prior written consent.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-1169515519`,
                  `The Klyk Service will not operate on a continuous basis and may be unavailable from time to time (including for maintenance purposes), without prior notice or liability to you. In addition, we may alter, amend, modify, or update any aspect or part of the Klyk Service at any time in our sole discretion, and that Klyk may, at its sole discretion, permanently or temporarily discontinue or suspend the Klyk Service, or any element of the Klyk Service, at any time without prior notice or liability to you.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms1358705994`,
                  `You acknowledge that we are not liable for any loss or damage whatsoever, howsoever caused, resulting from:`,
                )}
              </P>
              <ul>
                <li>
                  <P>
                    {t(
                      `terms-1638090341`,
                      `any action taken or reliance made by you on the functionality of any part or whole of the Klyk Service; or`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms883757415`,
                      `any action taken or reliance made by you on any content, video, information, or material on the Klyk Service.`,
                    )}
                  </P>
                </li>
              </ul>
            </li>
            <li>
              <P>
                {t(
                  `terms1314182723`,
                  `You acknowledge that Klyk is not responsible for any interactions or communications that you may have with any user on the Klyk Service. Klyk is not directly involved in any communications between you and any other user. Although Klyk will use reasonable endeavours to monitor and remove any content or material that infringes our Terms or Policies, you acknowledge that Klyk is not responsible for the acts or omissions of any user. Klyk makes no representations about the suitability, reliability, or accuracy of any content or information provided by any user whether provided on the Klyk Service or in interactions between you and the user whether via the Klyk Service or otherwise. You hereby indemnify Klyk against any claim, injury, damage or cost arising out of any communications you may have with any user through or in connection to the Klyk Service.`,
                )}
              </P>
            </li>
          </ul>

          {/* User content */}
          <H2>{t(`terms-1060972342`, `4. User Content`)}</H2>
          <ul>
            <li>
              <P>
                <Trans i18nKey="terms-2021823988">
                  You may provide, submit, upload, post, publish, livestream, or
                  communicate any original material or content, owned or
                  properly licensed by you, to or via the Klyk Service (&quot;
                  <strong>User Content</strong>&quot;). Nothing herein transfers
                  the ownership of any User Content to us.
                </Trans>
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms886401580`,
                  `By creating a Klyk Account, you grant us a perpetual, worldwide, non-exclusive, transferable, sub-licensable, royalty-free right and licence to livestream, transmit, host, store, use, copy, display, reproduce, adapt, edit, publish, communicate to the public, perform in public, modify, and distribute your User Content via the Klyk Service. This licence is granted by you to us for the limited purpose of operating, developing, providing, and improving the Klyk Service and researching and developing new services. You agree that any User Content may be viewed by other users and including any person visiting or participating in the Klyk Service.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms1772350856`,
                  `You are solely responsible for all User Content. You warrant that you have all rights necessary in the User Content and you warrant that the User Content does not violate the rights of any third party, any agreements, or any applicable laws. In addition you agree that you will be solely responsible for the payment of any royalties, fees or charges that are owed to any third party (including but not limited to any music collecting society) in connection to any User Content.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-757468573`,
                  `You agree that you will not add/remove any User Content to/from the Klyk Service:`,
                )}
              </P>
              <ul>
                <li>
                  <P>
                    {t(
                      `terms-623306030`,
                      `unless you hold all necessary rights, licences and consents to do so;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-1772793667`,
                      `that would cause you or us to breach any law, regulation, rule, code or other legal obligation;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1136331092`,
                      `that would be in breach of these Terms, including our Privacy Policy;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>{t(`terms-558390991`, `which:`)}</P>
                  <ul>
                    <li>
                      <P>
                        {t(
                          `terms-1051371555`,
                          `infringes the intellectual property or other rights of any third party, or otherwise links to infringing or unauthorised content;`,
                        )}
                      </P>
                    </li>
                    <li>
                      <P>
                        {t(
                          `terms-81738971`,
                          `brings or could bring Klyk or any Host into disrepute;`,
                        )}
                      </P>
                    </li>
                    <li>
                      <P>
                        {t(
                          `terms582458929`,
                          `is or could reasonably be considered to be deceptive, misleading, false, fraudulent, obscene, defamatory, disparaging, libellous, seditious, offensive, scandalous, sexually explicit, blasphemous threatening, harassing, vulgar, indecent, profane, hateful, degrading, discriminatory, intimidating, graphically violent, liable to incite racial hatred, racially or ethnically offensive, encourages criminal conduct, or is otherwise inappropriate;`,
                        )}
                      </P>
                    </li>
                    <li>
                      <P>
                        {t(
                          `terms1300379208`,
                          `creates or attempts to create any commercial affiliation where one does not exist;`,
                        )}
                      </P>
                    </li>
                    <li>
                      <P>
                        {t(
                          `terms-2078341638`,
                          `unlawfully impersonates any person (including by way of celebrity look-a-likes or sound-a-likes), or states or misrepresents any affiliation with any person where such affiliation does not exist;`,
                        )}
                      </P>
                    </li>
                    <li>
                      <P>
                        {t(
                          `terms1926447268`,
                          `depicts, encourages, promotes or induces conduct that could violate any law or give rise to criminal or civil liability;`,
                        )}
                      </P>
                    </li>
                    <li>
                      <P>
                        {t(
                          `terms145026261`,
                          `contains any personally identifying or other personal information about you or any other person except as expressly requested by us for the provision of Klyk Service to you;`,
                        )}
                      </P>
                    </li>
                    <li>
                      <P>
                        {t(
                          `terms367068331`,
                          `is in breach of confidence or in breach of privacy, or contains any confidential information, material, or non-public information about any individual, person, or company without express approval of the individual, person, or company;`,
                        )}
                      </P>
                    </li>
                    <li>
                      <P>
                        {t(
                          `terms639149193`,
                          `contains or omits any information or instruction that might be injurious to any person’s wellbeing; or`,
                        )}
                      </P>
                    </li>
                    <li>
                      <P>
                        {t(
                          `terms-550424435`,
                          `is otherwise objectionable to Klyk in its sole discretion.`,
                        )}
                      </P>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              <P>
                {t(
                  `terms-638635566`,
                  `You understand and agree that we may, but are not obligated to, monitor or review any User Content. We may delete any User Content, in whole or in part that, in our sole judgment, violates these Terms or may harm the reputation of Klyk or the Klyk Service.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms113610584`,
                  `Although we reserve the right to review, edit, and/or remove User Content that violates these Terms, such User Content is the sole responsibility of the user who posts it, and we cannot guarantee that all users will comply with these Terms. If you see any content on the Klyk Service that violates these Terms, please report it to admin@ourklyk.com. For the avoidance of doubt, under no circumstances will Klyk be liable in any way for any User Content including, but not limited to, any errors or omissions in any User Content, or any loss or damage of any kind incurred as a result of the use of any User Content posted, emailed, transmitted or otherwise made available on or through the Klyk Service.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms2096678984`,
                  `We may terminate or suspend your Account with no refund of any payments to you if you breach this clause 4.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-614918278`,
                  `If you believe that someone has breached these Terms or any of our Policies, please notify us by emailing admin@ourklyk.com.`,
                )}
              </P>
            </li>
          </ul>

          {/* Ownership */}
          <H2>
            {t(`terms1091039321`, `5. Ownership of Klyk Intellectual Property`)}
          </H2>
          <ul>
            <li>
              <P>
                <Trans i18nKey="terms1854641178">
                  You acknowledge that Klyk owns or licences all legal right,
                  title and interest in and to the Klyk Service and all
                  materials on the Klyk Service (other than User Content),
                  including all intellectual property rights which subsist in
                  the Klyk Service including but not limited to all data, text,
                  graphics, logos, photos, music, lyrics, recordings, videos,
                  software, downloads, look, feel, chats, etc, (the &quot;
                  <strong>Klyk Materials</strong>&quot;). Unless approved in
                  writing by Klyk, you hereby agree not to sell, license,
                  distribute, copy, modify, perform, edit or otherwise make use
                  of the Klyk Materials. Your access to the Klyk Service and the
                  Hosted Livestream does not give you any ownership rights to
                  the Klyk Service or the Hosted Livestream.
                </Trans>
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-799370153`,
                  `In using the Klyk Service you shall not, or permit anyone else to, copy, modify, translate, adapt or otherwise create derivative works or improvements, reverse engineer, disassemble, decompile, decode or otherwise attempt to derive or gain access to the source code of the Klyk Service or any part thereof. In addition, you agree not to remove, delete, alter or obscure any trademarks or any copyright, trademark, patent or other intellectual property or proprietary rights notices on the Klyk Service.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-1621937055`,
                  `You agree not to use any Klyk Materials in a way that is likely or intended to cause confusion about the owner of the intellectual property of such materials.`,
                )}
              </P>
            </li>
          </ul>

          {/* Payments Made on the Klyk Service */}
          <H2>
            {t(`terms-323939880`, `6. Payments Made on the Klyk Service`)}
          </H2>
          <ul>
            <li>
              <P>
                <Trans i18nKey="terms-1114792127">
                  You acknowledge that Klyk may charge or introduce a fee or
                  fees to access or use any part or whole of the Klyk Service (“
                  <strong>Fees”</strong>). Fees for access or use of the Klyk
                  Service will be published on www.ourklyk.com.
                </Trans>
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms116553266`,
                  `You acknowledge that any Fees paid by you is non-refundable and non-returnable.`,
                )}
              </P>
            </li>
            <li>
              <P>
                <Trans i18nKey="terms20211224161053119323746">
                  Klyk may utilise third party payment platforms to process Fees
                  (“<strong>Payment Partner</strong>”). Any Fees processed
                  through a Payment Partner will be in accordance with the
                  general terms of that Payment Partner. You are responsible for
                  making your own enquiries into the terms and conditions of the
                  relevant Payment Partner. All enquires or issues relating to
                  processing of Fees must be directed to the Payment Partner. In
                  no event shall Klyk be responsible or liable for any Fee
                  processing issue or error through a Payment Partner.
                </Trans>
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-2013999165`,
                  `You acknowledge, confirm, and agree that:`,
                )}
              </P>
              <ul>
                <li>
                  <P>
                    {t(
                      `terms-364653069`,
                      `all details you provide to Klyk and the Payment Partner are true and accurate, that you are an authorised user of the credit or debit card used make payments to Klyk and that there are sufficient funds to cover the cost of the Klyk Service;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1840070921`,
                      `foreign exchange rates may fluctuate and that all charges are subject to such changes; and`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1840070921`,
                      `foreign exchange rates may fluctuate and that all charges are subject to such changes; and`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-1025838898`,
                      `we may, at our sole and absolute discretion, increase, decrease, modify, alter, introduce or remove any fee or tariff charged by us, either permanently or temporarily.`,
                    )}
                  </P>
                </li>
              </ul>
            </li>
          </ul>

          {/* Paid Events */}
          <H2>{t(`6A.  Paid Events`, `6A.  Paid Events`)}</H2>
          <ul>
            <li>
              <P>
                <Trans i18nKey="terms20211224160923618797632">
                  From August, 2020 Klyk will be launching their &lsquo;Paid
                  Events&rsquo; feature, which allows &lsquo;host&rsquo; users (
                  <b>Hosts</b>) to organise a ticketed event on the Klyk Service
                  and charge &lsquo;participant&rsquo; users (
                  <b>Participants</b>) a fee for attendance.
                </Trans>
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms1190728537`,
                  `Tickets sold through the Klyk Service may not, without the prior written consent of Klyk, be re-sold or offered for resale on any third party platform (including via online auction sites). If a ticket is sold or offered for re-sale by a third party, or is otherwise used in breach of this condition, the ticket will be invalid and the holder may be refused admission to the event.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-2025654984`,
                  `Tickets will be registered in the Participant’s name, and each Participant is responsible for checking that all information provided to Klyk at the time of purchase is correct. The name to which the ticket is registered may only be changed by contacting Klyk, and administration fees may apply for processing such request. `,
                )}
              </P>
            </li>

            <li>
              <P>
                {t(
                  `terms511253891`,
                  `Klyk may utilise a Payment Partner to facilitate transactions. The final ticket price as advertised to you  at check-out is inclusive of processing and transaction fees that may be charged by a Payment Partner in connection with the sale.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-2043793950`,
                  `All monies from ticket sales less the "Klyk Service Fee" (as notified to the relevant Host at the time of creating the event)  will be deposited into the relevant Host's holding account with our Payment Partner  (each, an  "Account"). All monies in the relevant Account will be released to the relevant Host within thirty (30) days of the conclusion of the event, after deduction of any fees charged by the applicable Payment Partner.  The Host is responsible for any  currency conversion fees or other bank charges that may apply to payments outside of Australia.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-323889869`,
                  `If the Host cancels the event, the Participant will be entitled to a refund of the full ticket price  less any fees charged by a Payment Partner. If the Host cancels the event less than 24 hours prior to the scheduled time of the event, Klyk may retain the Klyk Service Fee, and the Host will be solely responsible for paying any difference on the refunded amount to the Participant so that the Participant receives a full refund of the ticket price (less any fees charged by a Payment Partner). Rescheduling of the event by the Host will be deemed a cancellation for the purpose of this clause.`,
                )}
              </P>
            </li>

            <li>
              <P>
                {t(
                  `terms-112668649`,
                  `The tickets are not exchangeable or redeemable for any other event, or any other consideration, monetary or otherwise.`,
                )}
              </P>
            </li>
            <li>
              <P>
                <Trans i18nKey="terms-1080953856">
                  Except where required by law, there will be no refund of any
                  tickets other than as provided for in 6.6A. If you would like
                  to make a complaint in connection with the Klyk Service, such
                  complaint must be emailed to
                  <Hyperlink href="mailto:admin@ourklyk.com">
                    admin@ourklyk.com
                  </Hyperlink>
                  within 48 hours of the occurrence of such complaint. Where
                  complaints relate to the event itself, your complaint must be
                  directed to the Host.
                </Trans>
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms309101605`,
                  `The event is provided &lsquo;as is&rsquo; and to the extent permittable by law, Klyk makes no warranties or representations regarding any events, and you have not relied on any representations or warranties by Klyk in connection with your choice to host an event or purchase an event ticket. Notwithstanding, if any person claims that Klyk has breached a term, condition, warranty, statement, or assurance which cannot be excluded by these Terms, to the maximum extent permitted by law, Klyk's entire liability and that person's exclusive remedy is limited to, in the case of a Host, a refund of the Klyk Service Fee, or in the case of a participant, a refund of the ticket price (less fees charged by any Payment Partner). `,
                )}
              </P>
            </li>

            <li>
              <P>
                {t(
                  `terms-1209120362`,
                  `Hosts may have their own terms and conditions in connection with an event, and you are solely responsible for making your own enquiries into any terms or conditions that may be stipulated by the Host. If there is any conflict between the terms and conditions of a Host and these Terms, these Terms shall prevail.`,
                )}
              </P>
            </li>
          </ul>

          {/* Warranties and Responsibilities */}
          <H2>{t(`terms1752692434`, `7. Warranties and Responsibilities`)}</H2>
          <ul>
            <li>
              <P>
                {t(
                  `terms-1289216575`,
                  `You represent and warrant that you will not, in connection to the Klyk Service:`,
                )}
              </P>
              <ul>
                <li>
                  <P>
                    {t(
                      `terms-1551830194`,
                      `copy, reproduce, redistribute, record, download, transfer, communicate to the public, perform or display to the public, broadcast or otherwise make available to the public any part of the Klyk Service or the Hosted Livestream, or otherwise make use of the Klyk Service or the Hosted Livestream in a manner that is not expressly permitted by these Terms;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms187203515`,
                      `use, share, upload, create or deal in any way with any material which infringes or violates any third party rights, including the intellectual property rights in the Klyk Service or the Hosted Livestream;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-13186190`,
                      `sell, rent, licence or leasing of any part of the Klyk Service or the Hosted Livestream;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-118893682`,
                      `circumvent, restrict, interfere or disable the security-related features of the Klyk Service;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-1536420725`,
                      `harvest or collect personal information about other users whether or not for commercial purposes, without Klyk’s written consent;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-715886961`,
                      `falsely state or otherwise misrepresent your affiliation with a person or entity;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-545858429`,
                      `threaten, stalk, intimidate, harass, bully, assault, pester or otherwise do anything else unwanted to another person;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-406948516`,
                      `use offensive, vulgar, profane, invasive, abusive or obscene language or gestures;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-1941827735`,
                      `use racial, religious or sexually offensive language or gestures;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-2073398686`,
                      `use, share, upload, create or deal in any way with material that is defamatory, libellous, derogatory, malicious, demeaning or hateful;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1881515744`,
                      `use any robot, spider, website search/retrieval application, or other manual or automatic device or process to retrieve, index, ‘data mine’, ‘scrape’ or in any way reproduce or circumvent the navigational structure or presentation of any part or whole of the Klyk Service or its contents;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-659642353`,
                      `interfere with or disrupt the Klyk Service, or the servers or networks connected to the Klyk Service;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms841130308`,
                      `post, email or otherwise transmit any material that contains software viruses, ‘Trojan horses’, ‘copybots’, ‘worms’, ‘spyware’, ‘time bombs’, ‘cancelbots’ or any other computer code, files or programs designed to interrupt, destroy or limit the functionality of any software, hardware or telecommunications equipment on the Klyk Service;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1744483101`,
                      `forge headers or otherwise manipulate identifiers in order to disguise the origin of any information transmitted through the Klyk Service;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-1924192322`,
                      `‘frame’ or ‘mirror’ any part of the Klyk Service, without Klyk’s prior written authorisation. Users also shall not use meta tags or code or other devices containing any reference to the Klyk Service in order to direct any person to any other website for any purpose;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-329083995`,
                      `directly or indirectly send ‘spam’, ‘junk mail’, ‘chain letters’, ‘pyramid schemes’ or any other form of solicitation, unauthorised advertisements or unauthorised promotion;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms2106364172`,
                      `intentionally damage or interfere with the Klyk Service or the use of the Klyk Service by any means, including but not limited to via use of viruses, adware, spyware, worms or malicious code;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-371093304`,
                      `take action to circumvent any measures that Klyk may use to restrict access to the Klyk Service.`,
                    )}
                  </P>
                </li>
              </ul>
            </li>
            <li>
              <P>
                {t(
                  `terms-580778421`,
                  `You agree that Klyk reserves all rights and remedies against you if you violate these Terms.`,
                )}
              </P>
            </li>
            <li>
              <P>
                <strong>
                  {t(
                    `terms14667758`,
                    `YOU ARE SOLELY RESPONSIBLE FOR YOUR INTERACTIONS WITH OTHER USERS AND/OR HOSTS. YOU UNDERSTAND THAT WE DO NOT CONDUCT CRIMINAL BACKGROUND CHECKS ON OUR USERS OR HOSTS OR OTHERWISE INQUIRE INTO THEIR BACKGROUND. WE MAKE NO REPRESENTATION OR WARRANTY AS TO THE CONDUCT OF USERS AND/OR HOSTS. WE RESERVE THE RIGHT TO CONDUCT ANY CRIMINAL BACKGROUND CHECK OR OTHER SCREENINGS (SUCH AS SEX OFFENDER REGISTER SEARCHES) AT ANY TIME USING AVAILABLE PUBLIC RECORDS BUT ARE UNDER NO OBLIGATION TO DO SO.`,
                  )}
                </strong>
              </P>
            </li>
            <li>
              <P>
                <strong>
                  {t(
                    `terms-943817100`,
                    `THE KLYK SERVICE AND ALL INFORMATION AND MATERIALS PROVIDED THROUGH THE SAME ARE PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS WITHOUT ANY WARRANTY OR CONDITION, EXPRESS OR IMPLIED. TO THE MAXIMUM EXTENT PERMISSIBLE UNDER APPLICABLE LAW, KLYK, AND EACH OF ITS JOINT VENTURERS, LICENSORS, SUPPLIERS, VENDORS, DISTRIBUTORS, ADVERTISERS AND OTHER CONTRACTING PARTIES, SPECIFICALLY DISCLAIM ALL WARRANTIES INCLUDING, WITHOUT LIMITATION, IMPLIED WARRANTIES OF TITLE, QUALITY, PERFORMANCE, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. FURTHER, NO WARRANTIES SHALL BE IMPLIED BY ANY COURSE OF DEALING OR COURSE OF PERFORMANCE.`,
                  )}
                </strong>
              </P>
            </li>
          </ul>

          {/* Procedure */}
          <H2>
            {t(
              `terms846880459`,
              `8. Procedure for Reporting Copyright Infringement`,
            )}
          </H2>
          <ul>
            <li>
              <P>
                <Trans i18nKey="terms708501590">
                  If you believe that any materials or content hosted on the
                  Klyk Service infringes your copyright (or the copyright of
                  someone on whose behalf you are authorised to act), please
                  send a notice of copyright infringement (&quot;
                  <strong>Infringement Notice</strong>&quot;) bearing a
                  signature (or electronic equivalent) of the copyright holder
                  or an authorised representative, and:
                </Trans>
              </P>
              <ul>
                <li>
                  <P>
                    {t(
                      `terms-1989691751`,
                      `identification of the copyrighted work or works that you claim has been infringed;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms607379061`,
                      `indicate where on the Klyk Service the infringing materials are located;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms866798965`,
                      `provide your full name and contact information (address, email address and telephone number);`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1615389689`,
                      `indicate that you are the lawful copyright owner or are authorised to act on the owner's behalf;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms733540999`,
                      `provide the following statements in the body of the Infringement Notice:`,
                    )}
                  </P>
                  <ul>
                    <li>
                      <P>
                        {t(
                          `terms310255396`,
                          `I hereby state in good faith that I believe that the use of work(s) is not authorised by the copyright owner, its agent ort the law.`,
                        )}
                      </P>
                    </li>
                    <li>
                      <P>
                        {t(
                          `terms-685879407`,
                          `I hereby state that the information in this Infringement Notice is accurate and, under penalty of perjury, that I am the owner, or authorised to act on behalf of the owner, of the work(s).`,
                        )}
                      </P>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              <P>
                {t(
                  `terms-1473425208`,
                  `If you believe that someone has wrongly filed an Infringement Notice against you, you may send us a counter-notice in accordance with our requirements. Our contact for copyright issues relating to the Klyk Service (including the notices and counter-notices) is admin@klyk.com.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-1206850110`,
                  `Upon receipt of the Infringement Notice containing the information outlined above, Klyk may:`,
                )}
              </P>
              <ul>
                <li>
                  <P>
                    {t(
                      `terms-479707503`,
                      `remove or disable access to the material that is alleged to be infringing;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms290317154`,
                      `forward the Infringement Notice to the alleged infringer; and`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms-1428790432`,
                      `take reasonable steps to promptly notify the alleged infringer that it has removed or disabled access to the material.`,
                    )}
                  </P>
                </li>
              </ul>
            </li>
          </ul>

          {/* Termination */}
          <H2>{t(`9. Termination`, `9. Termination`)}</H2>
          <ul>
            <li>
              <P>
                {t(
                  `terms1254935394`,
                  `You agree and acknowledge that Klyk may suspend or terminate any Account that you may have with Klyk or your use of the Klyk Service and remove or suspend any part or all of your Account at any time for any reason, including but not limited to if Klyk believes that you have breached these Terms, without notice to you. You agree that Klyk will not be liable to you or any third parties for any such termination or suspension and that any termination or suspension does not limit any other remedies that Klyk may have against you at law.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms1395116292`,
                  `You may terminate your account at any time via your account setting or by sending an email to admin@Klyk.com.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-352293905`,
                  `You agree that these Terms remain in effect after your account is terminated or suspended.`,
                )}
              </P>
            </li>
          </ul>

          {/* Third-Party */}
          <H2>
            {t(
              `terms2144852979`,
              `10. Third-Party Websites, API’s, Platforms, Plug-ins, and Services`,
            )}
          </H2>
          <ul>
            <li>
              <P>
                {t(
                  `terms-752710044`,
                  `The Klyk Service may contain links to services operated by third parties. Those links are provided for convenience and may not remain current or be maintained. Klyk does not endorse any of these third parties or the information, products or services that they provide. Any correspondence between you and the third party are solely between you and the third party and your access and use of the third party services are solely at your own risk. Unless expressly stated otherwise, we do not endorse and are not responsible for the content on those linked websites and have no control over or rights in those linked websites.`,
                )}
              </P>
            </li>
            <li>
              <P>
                <Trans i18nKey="terms1645015611">
                  The Klyk Service contains certain applications, API’s,
                  plugins, and services which may be operated by third parties
                  (“<strong>Plug-in</strong>”). Content and information on
                  Plug-ins is controlled by the owner of such Plugins (“
                  <strong>Plug-in Operator</strong>”). Your use of any Plug-in
                  (including any fees charged by the Plug-in Operator will be in
                  accordance with the terms of use set out by the Plug-in
                  Operator. You are responsible for making your own enquiries
                  into the terms and conditions of the relevant Plug-in. All
                  enquires or issues relating to a Plug-in must be directed to
                  the Plug-in Operator. In no event shall Klyk be responsible or
                  liable in any manner whatsoever in connection to your use or
                  non-use of any Plug-in.
                </Trans>
              </P>
            </li>
          </ul>

          {/* Mobile Devices */}
          <H2>{t(`terms-2040943880`, `11. Mobile and Other Devices`)}</H2>
          <ul>
            <li>
              <P>
                {t(
                  `terms563899499`,
                  `Please be aware that when you access or use the Klyk Service on your mobile or other personal device that your carrier's normal rates and fees, such as text messaging and data charges, will still apply.`,
                )}
              </P>
            </li>
          </ul>

          {/* Disclaimer */}
          <H2>{t(`12. Disclaimer`, `12. Disclaimer`)}</H2>
          <ul>
            <li>
              <P>
                {t(
                  `terms-918698040`,
                  `We are not liable to you or anyone else if interference with or damage to your computer systems occurs in connection with the use of the Klyk Service or a third-party linked website. You must take your own precautions to ensure that whatever you select for your use of the Klyk Service is free of viruses or anything else (such as worms or Trojan horses) that may interfere with or damage the operations of your computer systems.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms5672902`,
                  `We may, from time to time and without notice, change or add to the Klyk Service (including these Terms) or the information, products or services described in it. However, we do not undertake to keep the Klyk Service updated. We are not liable to you or anyone else in relation to any error or omission in connection to the Klyk Service, including if certain content or information is not up-to-date.`,
                )}
              </P>
            </li>
          </ul>

          {/* Limitation */}
          <H2>
            {t(`terms1780936500`, `13. Limitation of Liability and Indemnity`)}
          </H2>
          <ul>
            <li>
              <P>
                {t(
                  `terms-192625957`,
                  `To the maximum extent permitted by Law, Klyk, its officers, directors, employees, affiliates, agents, licensors and assignees disclaim all liability, whether based in contract, tort (including negligence), strict liability or otherwise, and further excludes liability for any incidental, consequential or punitive damages arising out of these Terms or your use or attempted use of the Klyk Service. To the extent permitted by law, Klyk’s liability for damages is hereby limited to the amount of money Klyk has earned through your use of Klyk.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms-1240512691`,
                  `To the extent permissible by law, you indemnify Klyk and its officers, directors, employees, affiliates, agents, licensors and assignees against any costs, expenses, losses, damages and liability arising out of:`,
                )}
              </P>
              <ul>
                <li>
                  <P>{t(`terms-1908451268`, `your breach of these Terms;`)}</P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1987700160`,
                      `your use or misuse of the Klyk Service;`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1999972527`,
                      `your breach of any law, regulation or third party right; or`,
                    )}
                  </P>
                </li>
                <li>
                  <P>
                    {t(
                      `terms1529178383`,
                      `any claim that you or your User Content caused damage to a third party.`,
                    )}
                  </P>
                </li>
              </ul>
            </li>
          </ul>

          {/* Miscellaneous */}
          <H2>{t(`14. Miscellaneous`, `14. Miscellaneous`)}</H2>
          <ul>
            <li>
              <P>
                {t(
                  `terms-1927534855`,
                  `These Terms, and any rights and licenses granted hereunder, may not be transferred or assigned by you, but may be assigned by Klyk without restriction.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms1899624564`,
                  `The failure to require performance of any provision shall not affect either Klyk’s or your right to require performance at any time thereafter, nor shall a waiver of any breach or default of these Terms constitute a waiver of any subsequent breach or default or a waiver of the provision itself. If any portion of these Terms is found to be unenforceable, such portion will be modified to reflect the parties’ intention and only to the extent necessary to make it enforceable, and the remaining provisions of this Agreement will remain in full force and effect.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms779573553`,
                  `These Terms shall be governed and construed in accordance with the laws of the New South Wales, Australia. If a court proceeding is permitted under these Terms, then you and Klyk agree to submit to the personal and exclusive jurisdiction and venue of the state and federal courts located within New South Wales, Australia.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms146400492`,
                  `In the interest of effective resolving disputes between you and Klyk, you and Klyk agree that if a dispute does arise out of these Terms or in relation to your use of Klyk’s, then you and Klyk agree to participate in binding arbitration before initiating court proceedings. This arbitration will be administered by the Australian Centre for International Commercial Arbitration (ACICA) in accordance with the ACICA Rules. Judgement on the arbitration award may be entered in any court with jurisdiction. Arbitrations may only take place on an individual basis and no class arbitrations or other grouping of parties is allowed.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms1232846219`,
                  `These Terms represent the entire agreement between you and Klyk relating to the subject matter herein and will not be modified except in writing, signed by both parties, or by a change to these Terms or policies made by Klyk as set forth above.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `terms653794854`,
                  `No agency, partnership, joint venture, employee-employer, or franchiser-franchisee relationship is intended or created by this Terms.`,
                )}
              </P>
            </li>
          </ul>
        </Div>
      </Section>
      <GetStarted />
    </Page>
  )
}

// Styled Components
const Div = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 664px;
  padding: 150px 0;

  h1 {
    margin-bottom: 60px;
  }
  h2 {
    margin-top: 60px;
  }
  h4 {
    margin-top: 30px;
  }
  p {
    margin-top: 15px;
  }
`

// Export.
export default TermsPage

export async function getStaticProps({ locale }) {
  const translations = await serverSideTranslations(locale, ['common'])
  return {
    props: {
      ...translations,
    },
  }
}
